import { useMemo } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Calendar } from "lucide-react";
import { useFormField } from "../../hooks/useFormField";
import { useIsMobile } from "../../hooks/useIsMobile";
import { FieldHint } from "../FieldHint";
import { inputClass, labelClass, resolveFieldState } from "./fieldStyles";
import type { IntakeFormData } from "../../types/form";
import type { useStepValidation } from "../../hooks/useStepValidation";

type StringField = {
  [K in keyof IntakeFormData]: IntakeFormData[K] extends string ? K : never;
}[keyof IntakeFormData];

interface DateInputProps {
  field: StringField;
  label: string;
  validation: ReturnType<typeof useStepValidation>;
  required?: boolean;
  /** Snap any selected date to YYYY-MM-01 and set min to first-of-last-month. */
  firstOfMonthOnly?: boolean;
  /** Minimum selectable date. Takes precedence over firstOfMonthOnly's auto min. */
  minDate?: Date;
  /** Maximum selectable date. Dates after this are disabled. */
  maxDate?: Date;
  /** Static helper text rendered between the label and the input. */
  hint?: string;
  containerClassName?: string;
}

function parseDate(ymd: string): Date | null {
  if (!ymd) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function formatYmd(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function firstOfLastMonth(now = new Date()): Date {
  return new Date(now.getFullYear(), now.getMonth() - 1, 1);
}

function snapToFirst(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

/**
 * Styled date picker using react-datepicker with Tailwind-themed calendar.
 * Stores value as YYYY-MM-DD string in the form store.
 *
 * Usage:
 *   <DateInput field="effectiveDate" label="Effective Date" validation={validation} required />
 *   <DateInput field="effectiveDate" label="Effective Date" validation={validation} firstOfMonthOnly />
 */
export function DateInput({
  field,
  label,
  validation,
  required,
  firstOfMonthOnly,
  minDate: minDateProp,
  maxDate,
  hint,
  containerClassName,
}: DateInputProps) {
  const f = useFormField(field, validation);
  const isMobile = useIsMobile();
  const minDate = useMemo(() => {
    const base = minDateProp ?? (firstOfMonthOnly ? firstOfLastMonth() : undefined);
    return firstOfMonthOnly && base ? snapToFirst(base) : base;
  }, [minDateProp, firstOfMonthOnly]);
  const filterDate = useMemo(
    () => (firstOfMonthOnly ? (d: Date) => d.getDate() === 1 : undefined),
    [firstOfMonthOnly],
  );
  const state = resolveFieldState({
    error: f.error,
    isMismatch: !!f.mismatch,
    isPrefilled: f.isPrefilled,
  });

  const selected = parseDate(f.value);

  return (
    <div className={containerClassName}>
      <label htmlFor={field} className={labelClass()}>
        {label}
        {required && <span className="text-danger" aria-hidden="true">&nbsp;*</span>}
      </label>
      {hint && <p className="text-xs text-gray-500 mb-1.5">{hint}</p>}
      {isMobile ? (
        <div className="relative focus-within:ring-2 focus-within:ring-brand-green-50 rounded-lg">
          {/* Styled display layer */}
          <div aria-hidden="true" className={`${inputClass(state)} cursor-pointer`}>
            {selected ? `${String(selected.getMonth() + 1).padStart(2, "0")}/${String(selected.getDate()).padStart(2, "0")}/${selected.getFullYear()}` : (
              <span className="text-gray-400">MM/DD/YYYY</span>
            )}
          </div>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <Calendar size={16} className="text-gray-400" />
          </span>
          {/* Invisible native input triggers OS date picker on tap */}
          <input
            type="date"
            id={field}
            aria-label={label}
            aria-invalid={f.error ? true : undefined}
            aria-describedby={f.error ? `${field}-hint` : undefined}
            autoComplete="off"
            value={f.value}
            onChange={(e) => {
              const val = e.target.value;
              if (!val) {
                f.setValue("" as IntakeFormData[typeof field]);
                return;
              }
              if (minDate && val < formatYmd(minDate)) {
                f.setValue("" as IntakeFormData[typeof field]);
                f.onBlur();
                return;
              }
              if (maxDate && val > formatYmd(maxDate)) {
                f.setValue("" as IntakeFormData[typeof field]);
                f.onBlur();
                return;
              }
              if (firstOfMonthOnly) {
                const picked = new Date(val + "T00:00:00");
                f.setValue(formatYmd(snapToFirst(picked)) as IntakeFormData[typeof field]);
              } else {
                f.setValue(val as IntakeFormData[typeof field]);
              }
              f.onBlur();
            }}
            min={minDate ? formatYmd(minDate) : undefined}
            max={maxDate ? formatYmd(maxDate) : undefined}
            className="absolute inset-0 opacity-0 cursor-pointer text-base"
          />
        </div>
      ) : (
        <div className="relative">
          <DatePicker
            id={field}
            selected={selected}
            ariaInvalid={f.error ? "true" : undefined}
            ariaDescribedBy={f.error ? `${field}-hint` : undefined}
            onChange={(date: Date | null) => {
              if (!date) {
                f.setValue("" as IntakeFormData[typeof field]);
                return;
              }
              const final = firstOfMonthOnly ? snapToFirst(date) : date;
              f.setValue(formatYmd(final) as IntakeFormData[typeof field]);
            }}
            onBlur={f.onBlur}
            onChangeRaw={(e) => {
              const target = e?.currentTarget as HTMLInputElement | undefined;
              if (target && target.value === "") {
                f.setValue("" as IntakeFormData[typeof field]);
              }
            }}
            autoComplete="off"
            minDate={minDate}
            maxDate={maxDate}
            filterDate={filterDate}
            dateFormat="MM/dd/yyyy"
            placeholderText="MM/DD/YYYY"
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            className={`${inputClass(state)} cursor-pointer`}
            calendarClassName="optimyl-datepicker"
            wrapperClassName="w-full"
          />
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <Calendar size={16} className="text-gray-400" />
          </span>
        </div>
      )}
      <FieldHint id={`${field}-hint`} error={f.error} isPrefilled={f.isPrefilled} mismatch={f.mismatch} />
    </div>
  );
}
